"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "~/components/ui/Button";
import { Section } from "~/components/ui/Section";
import { Text } from "~/components/ui/Text";
import { css, cx } from "~/generated/styled-system/css";
import { flex } from "~/generated/styled-system/patterns";
import { eyebrow, heading, paragraph } from "~/generated/styled-system/recipes";
import { useVisibleChildIndex } from "~/hooks/useVisibleChildIndex";
import { EmphasisInHeading } from "../../ui/EmphasisInHeading";
import testimonialsData from "./testimonials.json";
const TESTIMONIALS = testimonialsData;
const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
export function TestimonialsSection() {
  const $ = _c(47);
  const trackRef = useRef(null);
  const total = TESTIMONIALS.length;
  const active = useVisibleChildIndex(trackRef);
  const [atEnd, setAtEnd] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const track = trackRef.current;
      if (!track) {
        return;
      }
      setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 1);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const updateAtEnd = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const track_0 = trackRef.current;
      if (!track_0) {
        return;
      }
      updateAtEnd();
      const observer = new ResizeObserver(updateAtEnd);
      observer.observe(track_0);
      return () => observer.disconnect();
    };
    t2 = [updateAtEnd];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = idx => {
      const track_1 = trackRef.current;
      if (!track_1) {
        return;
      }
      const clamped = Math.max(0, Math.min(total - 1, idx));
      const slide = track_1.children[clamped];
      if (!(slide instanceof HTMLElement)) {
        return;
      }
      track_1.scrollTo({
        left: slide.offsetLeft,
        behavior: prefersReducedMotion() ? "auto" : "smooth"
      });
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const scrollToIndex = t3;
  let t4;
  if ($[4] !== active) {
    t4 = e => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        scrollToIndex(active + 1);
      } else {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          scrollToIndex(active - 1);
        }
      }
    };
    $[4] = active;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onKeyDown = t4;
  let t5;
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = css({
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: "6",
      "@/4xl": {
        gap: "8"
      },
      "@/6xl": {
        gap: "10"
      }
    });
    t6 = css({
      textAlign: "center"
    });
    t7 = <p className={cx(eyebrow(), css({
      marginBlockEnd: "3"
    }))}>What patients say</p>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = heading({
      size: "lg"
    });
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className={t6}>{t7}<h2 id="testimonials-heading" className={t8}>The work{" "}<EmphasisInHeading>changes</EmphasisInHeading>{" "}things.</h2></div>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = css({
      position: "relative",
      inlineSize: "full",
      maxInlineSize: "2xl",
      marginInline: "auto",
      "@/6xl": {
        maxInlineSize: "4xl"
      },
      borderRadius: "2xl",
      focusVisibleRing: "outside"
    });
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div ref={trackRef} onScroll={updateAtEnd} className={css({
      display: "flex",
      gap: "4",
      overflowX: "auto",
      scrollSnapType: "x mandatory",
      scrollbarWidth: "none",
      "@media (prefers-reduced-motion: no-preference)": {
        scrollBehavior: "smooth"
      },
      "&::-webkit-scrollbar": {
        display: "none"
      },
      "@/4xl": {
        gap: "5"
      },
      "@/6xl": {
        gap: "6"
      }
    })}>{TESTIMONIALS.map((t, i) => <div key={i} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${total}`} className={css({
        flex: "0 0 100%",
        scrollSnapAlign: "start",
        scrollSnapStop: "always",
        margin: 0,
        paddingBlock: "2",
        paddingInline: "1",
        "@/4xl": {
          paddingBlock: "4",
          paddingInline: "2"
        },
        "@/6xl": {
          flex: "0 0 calc(50% - 0.75rem)"
        }
      })}><span aria-hidden="true" className={css({
          display: "block",
          fontFamily: "display",
          textStyle: "display.lg",
          lineHeight: "0.8",
          color: "colorPalette.accentLarge",
          marginBlockEnd: "2"
        })}>“</span><blockquote className={cx(paragraph({
          size: "md",
          measure: "full"
        }), css({
          fontStyle: "italic",
          margin: 0,
          marginBlockEnd: "4",
          "@/4xl": {
            marginBlockEnd: "5"
          }
        }))}>{t.quote}</blockquote><p className={css({
          margin: 0
        })}><Text as="cite" size="sm" variant="accent" className={css({
            fontStyle: "normal",
            letterSpacing: "wide"
          })}>— {t.who}</Text></p></div>)}</div>;
    t12 = flex({
      alignItems: "center",
      justifyContent: "center",
      gap: "2",
      marginBlockStart: "6",
      "@/4xl": {
        marginBlockStart: "8"
      }
    });
    $[12] = t11;
    $[13] = t12;
  } else {
    t11 = $[12];
    t12 = $[13];
  }
  const t13 = active === 0;
  let t14;
  if ($[14] !== active) {
    t14 = () => scrollToIndex(active - 1);
    $[14] = active;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t13 || $[17] !== t14) {
    t15 = <CarouselButton direction="prev" disabled={t13} onClick={t14} />;
    $[16] = t13;
    $[17] = t14;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = css({
      letterSpacing: "wide",
      minInlineSize: "12",
      textAlign: "center",
      "@/3xl": {
        display: "none"
      }
    });
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const t17 = active + 1;
  let t18;
  if ($[20] !== t17) {
    t18 = <Text aria-hidden="true" size="sm" className={t16}>{t17} / {total}</Text>;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = flex({
      display: "none",
      alignItems: "center",
      "@/3xl": {
        display: "flex"
      }
    });
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== active) {
    t20 = TESTIMONIALS.map((_, i_0) => <button key={i_0} type="button" aria-current={active === i_0 ? "true" : undefined} aria-label={`Show testimonial ${i_0 + 1} of ${total}`} onClick={() => scrollToIndex(i_0)} data-active={active === i_0 || undefined} className={css({
      all: "unset",
      boxSizing: "border-box",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      inlineSize: "11",
      blockSize: "11",
      borderRadius: "full",
      focusVisibleRing: "outside",
      _before: {
        content: "\"\"",
        blockSize: "2",
        inlineSize: "2",
        borderRadius: "full",
        bg: "colorPalette.border",
        transitionProperty: "inline-size, background-color",
        transitionDuration: "240ms"
      },
      "&[data-active]::before": {
        inlineSize: "6",
        bg: "colorPalette.ink"
      },
      _hover: {
        _before: {
          bg: "colorPalette.ink"
        }
      },
      "@media (prefers-reduced-motion: reduce)": {
        _before: {
          transitionDuration: "0s"
        }
      }
    })} />);
    $[23] = active;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== t20) {
    t21 = <div role="group" aria-label="Select testimonial" className={t19}>{t20}</div>;
    $[25] = t20;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] !== active) {
    t22 = () => scrollToIndex(active + 1);
    $[27] = active;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== atEnd || $[30] !== t22) {
    t23 = <CarouselButton direction="next" disabled={atEnd} onClick={t22} />;
    $[29] = atEnd;
    $[30] = t22;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  let t24;
  if ($[32] !== t15 || $[33] !== t18 || $[34] !== t21 || $[35] !== t23) {
    t24 = <div className={t12}>{t15}{t18}{t21}{t23}</div>;
    $[32] = t15;
    $[33] = t18;
    $[34] = t21;
    $[35] = t23;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = css({
      position: "absolute",
      inlineSize: "1px",
      blockSize: "1px",
      padding: 0,
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: 0
    });
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const t26 = `Showing testimonial ${active + 1} of ${total}`;
  let t27;
  if ($[38] !== t26) {
    t27 = <div aria-live="polite" aria-atomic="true" className={t25}>{t26}</div>;
    $[38] = t26;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== onKeyDown || $[41] !== t24 || $[42] !== t27) {
    t28 = <div role="group" aria-roledescription="carousel" aria-label="Patient testimonials" tabIndex={0} onKeyDown={onKeyDown} className={t10}>{t11}{t24}{t27}</div>;
    $[40] = onKeyDown;
    $[41] = t24;
    $[42] = t27;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Text size="xs" className={css({
      textAlign: "center",
      letterSpacing: "wide"
    })}>Names and details changed to protect privacy.</Text>;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] !== t28) {
    t30 = <Section tone="secondary" variant="gradient" size="md" id="testimonials" aria-labelledby="testimonials-heading"><div className={t5}>{t9}{t28}{t29}</div></Section>;
    $[45] = t28;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  return t30;
}
function CarouselButton(t0) {
  const $ = _c(7);
  const {
    direction,
    disabled,
    onClick
  } = t0;
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  const t1 = direction === "prev" ? "Previous testimonial" : "Next testimonial";
  let t2;
  if ($[0] !== Icon) {
    t2 = <Icon size={20} aria-hidden="true" />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== disabled || $[3] !== onClick || $[4] !== t1 || $[5] !== t2) {
    t3 = <Button type="button" variant="outline" shape="circle" size="sm" aria-label={t1} disabled={disabled} onClick={onClick}>{t2}</Button>;
    $[2] = disabled;
    $[3] = onClick;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}